import { getUserInfo, type UserInfo, post, runAsyncSafe, getResultFromResponse, showLoginModal, showRegisterModal } from '@web3/utils';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';
import { useMemo } from 'react';
import { permissionStore } from './permission';
import { isBrowser } from '../utils/trick';

enum LoadingStage {
  // 刚创建，还没有做任何事
  initial = 0,
  // 数据是本地缓存
  localCache = 1,
  // 更新后的数据
  updated = 2,
}

export type UserInfoState = {
  stage: LoadingStage;
  loading: boolean;
  userInfo?: UserInfo;
  error?: unknown;
};

const computeState = (state: UserInfoState) => {
  const isReady = state.stage === LoadingStage.updated;
  const isInitial = state.stage === LoadingStage.initial;
  const isCache = state.stage === LoadingStage.localCache;
  const isLogin = Boolean(isReady && state.userInfo);
  return {
    isLogin,
    isReady,
    isCache,
    isInitial,
  };
};

type ComputedState = ReturnType<typeof computeState>;

class UserInfoStore extends EventBasedStore<UserInfoState, ComputedState> {
  constructor() {
    super(
      {
        stage: LoadingStage.initial,
        loading: false,
        userInfo: undefined,
        error: undefined,
      },
      computeState,
      () => {
        this.getUserInfo();
      },
    );
  }

  private _getInfoPromise?: Promise<UserInfo | undefined>;

  async getUserInfo() {
    if (!isBrowser) {
      return;
    }
    if (this._getInfoPromise) {
      return this._getInfoPromise;
    }
    try {
      this.setState({
        loading: true,
      });
      this._getInfoPromise = getUserInfo().finally(() => {
        this._getInfoPromise = undefined;
      });
      const userInfo = await this._getInfoPromise;
      this.setState({
        loading: false,
        userInfo: userInfo,
        stage: LoadingStage.updated,
      });
      return userInfo;
    } catch (err) {
      this.setState({
        loading: false,
        error: err,
        userInfo: undefined,
        stage: LoadingStage.updated,
      });
    }
  }

  async logout() {
    const [err] = await runAsyncSafe(getResultFromResponse(await post('/logout', {}, { withCredentials: true })));
    if (err) {
      // 登出错误应该不会发生吧
      return;
    }
    this.setState({
      userInfo: undefined,
    });
  }
  async login() {
    return new Promise((resolve, reject) => {
      showLoginModal(async () => {
        await this.getUserInfo().catch(reject);
        resolve(this.state.userInfo);
      });
    });
  }

  async register() {
    showRegisterModal(async () => {
      // 新注册用户若为荷兰IP重定向到荷兰语
      if (!permissionStore.state.countryCode) {
        return;
      }
      if (permissionStore.state.countryCode.toLocaleLowerCase() === 'nl') {
        window.location.href = `${window.location.origin}/nl-NL/${window.location.pathname.split('/').slice(2).join('/')}`;
        return;
      }
    });
  }
}

export const userInfoStore = new UserInfoStore();

export const useUserInfo = () => {
  const state = userInfoStore.useStoreState();
  const actions = useMemo(() => pickStoreActions(userInfoStore, ['getUserInfo', 'logout', 'login', 'register']), []);

  return {
    ...state,
    ...actions,
  };
};
